import {ofType} from 'redux-observable';
import {Observable} from 'rxjs';
import i18next from 'i18next';
import {catchError, map, flatMap} from 'rxjs/operators';

import {combineEpics} from 'web-app/util/redux-observable';
import * as ChildActions from 'web-app/react/entities/children/actions';
import * as GlobalEventActions from 'web-app/react/global-event/actions';
import {hasValue} from '@famly/stat_ts-utils_has-value';

import * as Api from './api';
import * as Actions from './actions';
import * as Helpers from './helpers';

export const fetchBillPayerObservable = (institutionId, billPayerId) =>
    Api.getBillPayer(institutionId, billPayerId).flatMap(({billPayer, children}) => [
        ChildActions.fetchAllSuccess.action(children),
        Actions.fetchSuccess.action({billPayer}),
    ]);

export const discontinueBillPayerObservable = (institutionId, billPayerId) =>
    Api.discontinueBillPayer(institutionId, billPayerId).map(response => [
        GlobalEventActions.updateSuccess.action(i18next.t('billPayer.deleteSuccess')),
        Actions.discontinueSuccess.action(response.billPayer),
    ]);

export const reviveBillPayerObservable = (institutionId, billPayerId) =>
    Api.reviveBillPayer(institutionId, billPayerId).map(response => [
        GlobalEventActions.updateSuccess.action(i18next.t('billPayer.restoreSuccess')),
        Actions.reviveSuccess.action(response.billPayer),
    ]);

export const fetchAllBillPayersObservable = (institutionId, childId, {includeRecipients} = {}) =>
    Api.fetchAllBillPayers(institutionId, childId, {
        includeRecipients,
    }).map(({billPayers, splitConfiguration, behaviors, recipients}) =>
        Actions.fetchAllSuccess.action({billPayers, splitConfiguration, childId, behaviors, recipients}),
    );

const fetchBillPayer = action$ =>
    action$
        .ofType(Actions.fetch.type)
        .mergeMap(({payload}) =>
            fetchBillPayerObservable(payload.institutionId, payload.billPayerId).catch(e =>
                Observable.of(Actions.fetchFailed.action(e)),
            ),
        );

const reviveBillPayer = action$ =>
    action$.ofType(Actions.revive.type).mergeMap(({payload}) =>
        reviveBillPayerObservable(payload.institutionId, payload.billPayerId)
            .flatMap(actions => actions)
            .catch(e => Observable.of(Actions.reviveFailed.action(e))),
    );

const discontinueBillPayer = action$ =>
    action$.ofType(Actions.discontinue.type).mergeMap(({payload}) =>
        discontinueBillPayerObservable(payload.institutionId, payload.billPayerId)
            .flatMap(actions => actions)
            .catch(e => Observable.of(Actions.discontinueFailed.action(e))),
    );

const fetchAllBillPayers = action$ =>
    action$
        .ofType(Actions.fetchAll.type)
        .mergeMap(({payload}) =>
            fetchAllBillPayersObservable(payload.institutionId, payload.childId).catch(e =>
                Observable.of(Actions.fetchAllFailed.action(e)),
            ),
        );

export const fetchBillPayersObservable = (
    targetId,
    limit,
    next,
    asOf,
    includeDeleted,
    onlyWithMissingAddress,
    paymentSourceTypes,
    includeCreditNotesAndCancelledInvoicesInBalance,
) =>
    Api.fetchBillPayers(
        targetId,
        limit,
        next,
        asOf,
        includeDeleted,
        onlyWithMissingAddress,
        paymentSourceTypes,
        includeCreditNotesAndCancelledInvoicesInBalance,
    ).map(({billPayers, balances, csv, next}) =>
        Actions.fetchBillPayersSuccess.action({billPayers, balances, csv, next, asOf}),
    );

const fetchBillPayers = action$ =>
    action$
        .ofType(Actions.fetchBillPayers.type)
        .mergeMap(({payload}) =>
            fetchBillPayersObservable(payload.targetId).catch(e =>
                Observable.of(Actions.fetchBillPayersFailed.action(e)),
            ),
        );

export const createObservable = (institutionId, childId, newBillPayerData) =>
    Api.createBillPayer(institutionId, newBillPayerData).map(billPayer =>
        Actions.createSuccess.action({billPayer, childId}),
    );

const updateFromDataObservable = (institutionId, billPayerData) =>
    Api.updateBillPayer(institutionId, billPayerData.billPayerId, billPayerData).map(billPayer =>
        Actions.updateSuccess.action({billPayer}),
    );

export const updateObservable = (institutionId, billPayer) => updateFromDataObservable(institutionId, billPayer.toJS());

const updateBillPayer = action$ =>
    action$.ofType(Actions.update.type).mergeMap(({payload}) => {
        return updateObservable(payload.institutionId, payload.billPayer).catch(e =>
            Observable.of(Actions.updateFailed.action(e)),
        );
    });

export const updateSplitObservable = (institutionId, childId, newSplitConfiguration) =>
    Api.updateSplit(institutionId, childId, newSplitConfiguration).map(splitConfiguration =>
        Actions.updateSplitSuccess.action({splitConfiguration, childId}),
    );

const updateSplitConfiguration = action$ =>
    action$.ofType(Actions.updateSplit.type).mergeMap(({payload}) => {
        return updateSplitObservable(payload.institutionId, payload.childId, payload.splitConfiguration).catch(e =>
            Observable.of(Actions.updateSplitFailed.action(e)),
        );
    });

export const createOrUpdateObservable = (institutionId, childId, newBillPayerData) => {
    if (newBillPayerData.billPayerId) {
        return updateFromDataObservable(institutionId, newBillPayerData);
    } else {
        return createObservable(institutionId, childId, newBillPayerData);
    }
};

export const addBillPayerToChildObservable = (
    institutionId,
    childId,
    existingChildBillPayers,
    existingChildSplitConfiguration,
    newBillPayerData,
    sensitiveInformation,
) => {
    return createOrUpdateObservable(institutionId, childId, newBillPayerData)
        .mergeMap(billPayerSuccessAction => {
            const newSplitConfiguration = Helpers.addBillPayerToSplitConfiguration(
                existingChildSplitConfiguration,
                billPayerSuccessAction.payload.billPayer,
            );

            return updateSplitObservable(institutionId, childId, newSplitConfiguration).map(successAction => {
                return {
                    successActions: [
                        billPayerSuccessAction,
                        successAction,
                        sensitiveInformation && billPayerSuccessAction.payload?.billPayer?.billPayerId
                            ? Actions.updateSensitiveInformation.action(
                                  institutionId,
                                  billPayerSuccessAction.payload.billPayer.billPayerId,
                                  sensitiveInformation,
                              )
                            : null,
                    ].filter(hasValue),
                };
            });
        })
        .map(({successActions}) => successActions.concat(Actions.addBillPayerToChildSuccess.action()));
};

const addBillPayerToChild = action$ =>
    action$.ofType(Actions.addBillPayerToChild.type).mergeMap(({payload}) => {
        return addBillPayerToChildObservable(
            payload.institutionId,
            payload.childId,
            payload.existingChildBillPayers,
            payload.existingChildSplitConfiguration,
            payload.newBillPayerData,
            payload.sensitiveInformation,
        )
            .flatMap(actions => actions)
            .catch(e => Observable.of(Actions.addBillPayerToChildFailed.action(e)));
    });

export const updateNoteObservable = (institutionId, billPayerNoteMap) =>
    Api.updateNote(institutionId, billPayerNoteMap).map(billPayers => Actions.updateNoteSuccess.action(billPayers));

const updateNote = action$ =>
    action$.ofType(Actions.updateNote.type).mergeMap(({payload}) => {
        return updateNoteObservable(payload.institutionId, payload.billPayerNoteMap).catch(e =>
            Observable.of(Actions.updateNoteFailed.action(e)),
        );
    });

const fetchSensitiveInformation = action$ =>
    action$.pipe(
        ofType(Actions.fetchSensitiveInformation.type),
        flatMap(({payload}) => {
            return Api.fetchSensitiveInformation(payload.institutionId, payload.billPayerId).pipe(
                map(sensitiveInformation => {
                    return Actions.fetchSensitiveInformationSuccess.action(payload.billPayerId, sensitiveInformation);
                }),
                catchError(e => Observable.of(Actions.fetchSensitiveInformationFailed.action(e))),
            );
        }),
    );

const updateSensitiveInformation = action$ =>
    action$.pipe(
        ofType(Actions.updateSensitiveInformation.type),
        flatMap(({payload}) => {
            return Api.updateSensitiveInformation(
                payload.institutionId,
                payload.billPayerId,
                payload.sensitiveInformation,
            ).pipe(
                flatMap(() => {
                    return Observable.of(Actions.updateSensitiveInformationSuccess.action());
                }),
                catchError(e => Observable.of(Actions.updateSensitiveInformationFailed.action(e))),
            );
        }),
    );

export default combineEpics(
    fetchBillPayers,
    fetchBillPayer,
    reviveBillPayer,
    discontinueBillPayer,
    fetchAllBillPayers,
    addBillPayerToChild,
    updateBillPayer,
    updateSplitConfiguration,
    updateNote,
    fetchSensitiveInformation,
    updateSensitiveInformation,
);
